if (!customElements.get("viewer-modal")) {
  class ViewerModal extends HTMLElement {
    static canvasCache = new Map();

    constructor() {
      super();
      this.materialHidden = false;
      this.viewer = null;
      this.apiDomain = null;
      this.nodeNames = null;
      this.canvasSlotsMaterials = null;
      this.canvasSlotsTags = null;
      this.handleWizardInfoToggled = this.handleWizardInfoToggled.bind(this);
      this.handleDyoDesignSelected = this.handleDyoDesignSelected.bind(this);
      this.handleViewerLoaded = this.handleViewerLoaded.bind(this);
      this.stitchesIndex = 3; // Assuming stitches are always at index 3, adjust as needed
      this._canvasId = null;
      this.viewerDataLoaded = null;
      this.previousSummary = null;
    }

    /**
     * canvasId property
     * - Getter: returns current canvas ID
     * - Setter: updates canvas ID and viewer attribute if changed
     */
    get canvasId() {
      return this._canvasId;
    }

    set canvasId(value) {
      if (!value || value === this._canvasId) return;
      this._canvasId = value;

      if (this.viewer) {
        this.viewer.id = value;
        this.viewer.setAttribute("data-canvas-id", value);
      }
    }

    connectedCallback() {
      this.viewer = this.querySelector("studio-viewer");
      this.apiDomain = this.viewer?.getAttribute("api-domain") || null;

      // Initialize canvasId using the setter
      const initialId = this.viewer?.getAttribute("data-canvas-id");
      if (initialId) {
        this.canvasId = initialId;
      }

      if (!this.viewer) {
        console.warn(
          "ViewerModal: No <studio-viewer> found inside <viewer-modal>."
        );

        return null;
      }

      this.viewer.addEventListener("loaded", this.handleViewerLoaded);
      window.addEventListener("wizard:change", this.handleWizardInfoToggled);
      window.addEventListener(
        "dyo-design:selected",
        this.handleDyoDesignSelected
      );
    }

    disconnectedCallback() {
      if (!this.viewer) return null;

      this.viewer.removeEventListener("loaded", this.handleViewerLoaded);
      window.removeEventListener("wizard:change", this.handleWizardInfoToggled);
      window.removeEventListener(
        "dyo-design:selected",
        this.handleDyoDesignSelected
      );
    }

    async handleFetchViewerData(id) {
      if (!id) return false;

      this.handleLoaderForDataLoading(true);

      // 1. Check Cache first
      if (ViewerModal.canvasCache.has(id)) {
        const cachedData = ViewerModal.canvasCache.get(id);
        this.applyCanvasData(cachedData);
        this.handleLoaderForDataLoading(false);
        return true;
      }

      // 2. Fetch from API if not cached
      try {
        const data = await fetch(`${this.apiDomain}/canvases/public/${id}`);
        const canvas = await data.json();

        if (data.ok) {
          ViewerModal.canvasCache.set(id, canvas);
          this.applyCanvasData(canvas);
        }
      } catch (error) {
        console.error(`Failed to fetch data from ${this.apiDomain}:`, error);
      } finally {
        this.handleLoaderForDataLoading(false);
      }

      return true;
    }

    /**
     * Helper to map API data to instance properties.
     * @param {Object} canvas
     */
    applyCanvasData(canvas) {
      this.nodeNames = canvas.nodeNames || [];
      this.canvasSlotsMaterials = canvas.canvasSlotsMaterials || [];
      this.canvasSlotsTags = canvas.canvasSlotsTags || [];
    }

    /**
     * Handles the viewer loaded event and sets all materials from previous summary.
     * @param {CustomEvent} event
     */
    handleViewerLoaded(e) {
      const viewer = e.target;
      const value = viewer.dataset?.canvasId || viewer.id;
    
      this.viewerDataLoaded = this.handleFetchViewerData(value).then(() => {
        if (Array.isArray(this.previousSummary)) {
          this.applyMaterialsFromSummary(this.previousSummary, viewer)
            .finally(() => this.handleLoaderForDataLoading(false));
        }
      });
    }    

    /**
     * Handles the wizard:change event from native-wizard.
     * @param {CustomEvent} event
     */
    handleWizardInfoToggled(event) {
      const { currentStep, summary } = event.detail || {};

      const filteredSummary = summary?.find(
        (data) => data.step === currentStep
      );

      const newCanvasId = filteredSummary?.canvasId || this.canvasId;

      if (newCanvasId && newCanvasId !== this.canvasId) {
        this.previousSummary = summary;
        this.canvasId = newCanvasId;
      } else {
        this.setMaterialFromSummaryItem(filteredSummary, this.viewer);
        this.handleLoaderForDataLoading(true);
      }
    }

    /**
     * Handles the dyo-design:selected event and sets all materials from summary.
     * @param {CustomEvent} event
     */
    handleDyoDesignSelected(event) {
      const { config } = event.detail?.summary || {};

      if (!Array.isArray(config)) return;

      this.previousSummary = config;

      // Wait for viewer data to be loaded
      Promise.resolve(this.viewerDataLoaded).then(() => {
        this.handleLoaderForDataLoading(true);
        const viewer = document.querySelector(
          "viewer-modal[data-pdp-canvas-modal] studio-viewer"
        );

        this.applyMaterialsFromSummary(config, viewer).finally(() => {
          this.handleLoaderForDataLoading(false);
        });
      });
    }

    /**
     * Helper to apply materials from summary
     * @param {Object} configItem
     */
    applyMaterialsFromSummary(summaryConfig, viewer) {
      if (!Array.isArray(summaryConfig)) return Promise.resolve();

      const filteredConfig = summaryConfig;

      // Chain material setting promises in order
      let chain = Promise.resolve();
      filteredConfig.forEach((item) => {
        chain = chain.then(() => this.setMaterialFromSummaryItem(item, viewer));
      });

      return chain;
    }

    /**
     * Helper to set material from a summary item.
     * @param {Object} configItem
     */
    setMaterialFromSummaryItem(configItem, viewer) {
      const slotName = configItem?.slotName || "";
      const tagName = configItem?.tagSlot || "";
      const materialName = configItem?.materialName || "";
      const isHideMaterial = configItem?.isHideMaterial;
      const targetIndex = this.nodeNames?.indexOf(slotName);

      const matchingTagSlot = this.canvasSlotsTags?.find((obj) => {
        return obj.tag.name.trim() === tagName?.trim();
      });

      const matchingMaterial = matchingTagSlot?.materials?.find((obj) => {
        return obj.name.trim() === materialName?.trim();
      });

      return this.setViewerMaterial(
        matchingMaterial?.id,
        targetIndex,
        isHideMaterial,
        viewer
      );
    }

    async setViewerMaterial(
      materialId,
      targetIndex,
      isHideMaterial = false,
      viewer = this.viewer
    ) {
      if (!viewer) {
        console.warn(
          "ViewerModal: No <studio-viewer> found inside <viewer-modal>. Cannot set material."
        );

        return null;
      }

      try {
        if (isHideMaterial) {
          this.materialHidden = true;
          await viewer.hideModelIndexes([targetIndex, this.stitchesIndex]);
        } else {
          await viewer.showModelIndexes([targetIndex, this.stitchesIndex]);
          await viewer.setMaterial(materialId, targetIndex);
        }
      } catch (error) {
        console.error(
          `Studio Viewer didn't load properly. Possibly metaobject variable names don't match the Studio3D variable names. Error: ${error.message}`
        );
      } finally {
        this.handleLoaderForDataLoading(false);
      }
    }

    handleLoaderForDataLoading(loading = true) {
      this.classList.toggle("viewer-data-loading", loading);
    }
  }

  customElements.define("viewer-modal", ViewerModal);
}
